import { Box } from "@mui/material";
import HomeTableSkeleton from "src/components/common/HomeTableSkeleton";
import { AxiosError, AxiosResponse } from "axios";
import { useState } from "react";
import { useQuery } from "react-query";
import { useStateOptions } from "src/hooks/useStateOptions";
import { createSearchParams, Link as RouterLink } from "react-router-dom";
import DashboardWrapper from "src/components/common/DashboardWrapper";
import DataGrid from "src/components/common/DataGrid";
import EmptyView from "src/components/common/EmptyView";
import { Button } from "src/components/common/FormComponents/Buttons";
import { getMonitoringLandOwnerships } from "src/utils/api/dashboard";
import {
  dateAndTimeStamp,
  downloadSheet,
  formatToIndianCurrency,
} from "src/utils/helper";
import { entityStatusMessages } from "src/constants/common";
import defaultStyles, { StylesClasses } from "./styles";
import { path } from "src/constants/path";
import SearchBar from "src/components/common/SearchField";
import MultiSelect from "src/components/common/MultiSelect";
import { getStyles } from "src/styles/theme";
import { useTableQuery } from "src/hooks/useTableQuery";
import { useLandOwnershipEntityOptions } from "src/hooks/useLandOwnershipEntityOptions";

const Monitoring = () => {
  const [tableRows, setTableRows] = useState<any[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const { states } = useStateOptions();

  const styles = getStyles<StylesClasses>(defaultStyles);

  const {
    queryParams,
    setQueryParams,
    paginationModel,
    filterKey,
    handleSearch,
    handleFilterChange,
    handleSort,
    handlePaginationChange,
    clampToTotal,
    searchDefaultValue,
  } = useTableQuery<{
    download: boolean;
    status: string;
    stateIds?: string[] | null;
    entityIds?: string[] | null;
  }>({
    pageKey: "monitoring",
    pageSize: 50,
    extraParams: {
      download: false,
      status: "APPROVED",
      stateIds: undefined,
      entityIds: undefined,
    },
    defaultFilterKey: { stateIds: null, entityIds: null },
  });

  const { entityInfo } = useLandOwnershipEntityOptions({
    status: queryParams.status,
    stateIds: queryParams.stateIds?.join(","),
    searchText: queryParams.searchText,
  });

  const { isLoading, isFetching } = useQuery(
    ["monitoringApprovedProjects", queryParams],
    () =>
      getMonitoringLandOwnerships({
        ...queryParams,
      }),
    {
      onSuccess: (res: AxiosResponse) => {
        const rows = res.data.rows.map((row: any) => ({
          sno: row?.serialNo,
          id: row.id,
          registrationNo: row.registrationNo,
          entityName: row.user?.firstName,
          state: row.state ?? "-",
          district: row?.district ?? "-",
          division: row?.projectDetail?.landSpecification?.division,
          totalArea: `${row.totalArea}`,
          finalArea: row?.netPlantationArea ?? "-",
          ecoRestorationCost: formatToIndianCurrency(row?.demandNote) ?? "-",
          adminCharges: formatToIndianCurrency(row?.icfreCharges) ?? "-",
          demandNote: row.demandNote
            ? formatToIndianCurrency(
                +`${Number(row?.demandNote)}` + +`${Number(row?.icfreCharges)}`,
              )
            : "-",
          totalAmountPaid: formatToIndianCurrency(row.amountPaid) ?? "-",
          ecoRestorationCostPaid:
            formatToIndianCurrency(row.ecoRestorationCost) ?? "-",
          adminChargesPaid: formatToIndianCurrency(row.adminCharges) ?? "-",
          status: entityStatusMessages[row.status] ?? row.status,
          created_at: dateAndTimeStamp(row?.created_at, "IST"),
          updated_at: dateAndTimeStamp(row?.updated_at, "IST"),
        }));

        if (queryParams.download) {
          const exportData = rows.map((item: any, index: number) => ({
            "S.No": index + 1,
            EPID: item?.id,
            "Registration No": item?.registrationNo,
            "Entity Name": item?.entityName,
            State: item?.state,
            District: item?.district,
            Division: item?.division,
            "Selected Area (Ha)": item?.totalArea,
            "Final Area (Ha)": item?.finalArea,
            "Eco-Restoration Cost (₹)": item?.ecoRestorationCost,
            "Admin Charges (₹)": item?.adminCharges,
            "Demand Note Amount (₹)": item?.demandNote,
            "Total Amount Paid (₹)": item?.totalAmountPaid,
            "Eco-Restoration Cost Paid (₹)": item?.ecoRestorationCostPaid,
            "Admin Charges Paid (₹)": item?.adminChargesPaid,
            Status: item?.status,
            "Created At": item?.created_at,
            "Updated At": item?.updated_at,
          }));
          downloadSheet(exportData);
          setQueryParams((prev) => ({
            ...prev,
            download: false,
          }));
        } else {
          const count = res.data.count || 0;

          if (clampToTotal(count)) {
            return;
          }

          setTableRows(rows);
          setRowCount(count);
        }
      },
      onError: (err: AxiosError) => {},
      refetchOnMount: true,
    },
  );

  const RenderEPID = (params: any) => {
    return (
      <Box
        component={RouterLink}
        to={`${path.landOwnershipDetails}?${createSearchParams({
          id: params?.row?.id,
        })}`}
        sx={{ ...defaultStyles.renderButtonText2 }}
      >{`${params?.row?.id}`}</Box>
    );
  };

  const RenderUploadReport = (params: any) => {
    return (
      <Box
        component={RouterLink}
        to={`${path.monitoringReport}?${createSearchParams({
          epid: params?.row?.id,
        })}`}
        sx={{ ...defaultStyles.renderButtonText2 }}
      >
        Upload Report
      </Box>
    );
  };

  const RenderOldMonitoring = (params: any) => {
    return (
      <Box
        component={RouterLink}
        to={`${path.monitoringDetails}?${createSearchParams({
          id: params?.row?.id,
        })}`}
        sx={{ ...defaultStyles.renderButtonText2 }}
      >
        View Details
      </Box>
    );
  };

  const tableColumns: any = [
    {
      field: "sno",
      headerName: "S.No",
      width: 90,
      sortable: false,
    },
    {
      field: "id",
      headerName: "EPID",
      width: 250,
      sortable: false,
      renderCell: RenderEPID,
    },
    {
      field: "oldMonitoring",
      headerName: "Monitoring Details",
      width: 170,
      sortable: false,
      renderCell: RenderOldMonitoring,
    },
    {
      field: "uploadReport",
      headerName: "Upload Report",
      width: 170,
      sortable: false,
      renderCell: RenderUploadReport,
    },

    {
      field: "registrationNo",
      headerName: "Reg.No",
      width: 100,
      sortable: false,
    },
    {
      field: "entityName",
      headerName: "Entity Name",
      width: 150,
      sortable: true,
    },
    {
      field: "state",
      headerName: "State",
      width: 150,
      sortable: true,
    },
    {
      field: "district",
      headerName: "District",
      width: 150,
      sortable: true,
    },
    {
      field: "division",
      headerName: "Division",
      width: 150,
      sortable: true,
    },
    {
      field: "totalArea",
      headerName: "Selected Area(Ha)",
      width: 170,
      sortable: false,
    },
    {
      field: "finalArea",
      headerName: "Final Area(Ha)",
      width: 150,
      sortable: false,
    },
    {
      field: "ecoRestorationCost",
      headerName: "Eco-Restoration Cost",
      width: 200,
      sortable: false,
    },
    {
      field: "adminCharges",
      headerName: "Admin Charges",
      width: 170,
      sortable: false,
    },
    {
      field: "demandNote",
      headerName: "Demand Note Amount",
      width: 200,
      sortable: false,
    },
    {
      field: "totalAmountPaid",
      headerName: "Total Amount Paid",
      width: 200,
      sortable: false,
    },
    {
      field: "ecoRestorationCostPaid",
      headerName: "Eco-Restoration Cost Paid",
      width: 220,
      sortable: false,
    },
    {
      field: "adminChargesPaid",
      headerName: "Admin Charges Paid",
      width: 200,
      sortable: false,
    },
    {
      field: "created_at",
      headerName: "Project Selection Date",
      width: 200,
      sortable: false,
    },
    {
      field: "updated_at",
      headerName: "Updated At",
      width: 200,
      sortable: true,
    },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      sortable: false,
    },
  ];

  const exportToExcelEntity = () => {
    setQueryParams((prev) => ({
      ...prev,
      download: true,
    }));
  };

  return (
    <DashboardWrapper
      title={"Monitoring"}
      buttonIcon="/assets/svg/roundPlusIcon.svg"
      buttonText=""
      onButtonClick={() => {}}
      showBtn={false}
      showBackBtn={true}
    >
      <Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            gap: "2%",
            padding: "10px 1%",
            background: "white",
            borderTopLeftRadius: "8px",
            borderTopRightRadius: "8px",
          }}
        >
          <SearchBar
            getText={handleSearch}
            defaultValue={searchDefaultValue}
            customStyles={{
              wrapper: {
                flex: 1,
                minWidth: "200px",
              },
            }}
          />
          <MultiSelect
            options={states}
            value={filterKey.stateIds}
            onChange={(e: any) =>
              handleFilterChange({ stateIds: e.length ? e : null })
            }
          />
          <MultiSelect
            options={entityInfo}
            label="Filter By Entity"
            clearable
            value={queryParams.entityIds ?? []}
            onChange={(e: string[]) =>
              handleFilterChange({ entityIds: e.length ? e : null })
            }
          />
          <Button onClick={exportToExcelEntity} text={"Export"} />
        </Box>
        {isLoading || isFetching ? (
          <HomeTableSkeleton showTabs={false} columns={8} />
        ) : tableRows.length > 0 ? (
          <DataGrid
            rows={tableRows}
            columns={tableColumns}
            rowCount={Math.ceil(rowCount / paginationModel.pageSize)}
            sortingMode="server"
            onSortModelChange={handleSort}
            pageSize={1}
            onChangePage={(page: number) =>
              handlePaginationChange({ ...paginationModel, page })
            }
            pageNumber={paginationModel.page + 1}
            loading={isLoading}
            columnBuffer={20}
          />
        ) : (
          <EmptyView
            heading="No Entry Found"
            subheading=""
            onClick={() => {}}
          />
        )}
      </Box>
    </DashboardWrapper>
  );
};

export default Monitoring;
